
import { useMemo, useState } from "react";

import PagedTable from "../../components/shared/Tables/PagedTable";
import TableCheckboxCol from "../../components/shared/Tables/IndeterminateCheckbox";
import { createColumnHelper, RowModel } from "@tanstack/react-table";
import { IPart, PartStatus } from "../../models/Part";
import PartImage from "../../components/stock/PartImage";
import ClickToCopy from "../../components/shared/ClickToCopy";
import WarehouseLocationFromId from "../../components/warehouseLocations/WarehouseLocationFromId";
import OrderStatus from "../../components/ebayOrders/OrderStatus";
import PartStatusDropdown from "../../components/stock/PartStatusDropdown";
import { PageMode } from "../../models/PagedTable";
import openInNewTab from "../../utils/openInNewTab";
import useCoreMutations from "../../data/useCoreMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ButtonGroup from "../shared/ButtonGroup";

const colHelper = createColumnHelper<IPart>();

export default function CoreStockTable() {
  const cols = useMemo(
    () => [
      TableCheckboxCol(false),
      colHelper.display({
        id: "images",
        cell: (info) => <PartImage part={info.row.original} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor("tagNumber", {
        header: "Tag No.",
        sortingFn: "auto",
      }),
      colHelper.accessor(row => row.category?.name, {
        header: "Category",
        id: "categoryId",
        sortingFn: "auto",
        cell: (info) => <span>{info.row.original.category?.name}</span>,
        sortDescFirst: false,
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: "Make, Model & Reg",
        enableSorting: false,
        cell: (info) => <div>
          <p>{info.row.original.vehicle?.combinedMake}</p>
          <p>{info.row.original.vehicle?.combinedModel}</p>
          <p>{info.row.original.vehicle?.dvlaYearOfManufacture}</p>
          <ClickToCopy text={info.row.original.vehicle?.vrm} />
        </div>,
      }),
      colHelper.accessor("enteredPrice", {
        header: "Price",
        sortingFn: "auto",
        cell: (info) => <div>£{info.row.original.enteredPrice?.toFixed(2)}</div>
      }),
      colHelper.accessor("warehouseLocationId", {
        header: "Warehouse",
        sortingFn: "auto",
        sortDescFirst: false,
        cell: (info) => <WarehouseLocationFromId id={info.getValue()} lastKnownId={info.row.original.lastKnownWarehouseLocationId} locatedById={info.row.original.locatedBy} lastStockChecked={info.row.original.lastStockChecked} />,
      }),
      colHelper.accessor("category", {
        header: "Fitment",
        enableSorting: false,
        cell: (info) => <div>
          <p>{info.getValue()?.mechOnly && "Mech"}</p>
          <p>{info.getValue()?.partOnly && "Body"}</p>
        </div>,
      }),
      colHelper.accessor("offers", {
        id: "order status",
        header: "Order Status",
        enableSorting: false,
        cell: (info) => <OrderStatus part={info.row.original} />,
      }),
      colHelper.accessor("partStatus", {
        header: "Part Status",
        enableSorting: false,
        cell: (info) => <PartStatusDropdown part={info.row.original} />,
      })
    ],
    []
  );

  const { bulkCompleteStock } = useCoreMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("parts");

  const [tab, setTab] = useState<"active" | "complete">("active");
  
  const options = useMemo(() => [
    { id: "active"  , label: "Active Stock"  , value: "active"   },
    { id: "complete", label: "Complete Stock", value: "complete" },
  ], []);

  const selectedIndex = tab === "active" ? 0 : 1;

  return (
    <>
      <ButtonGroup
        options={options}
        selectedIndex={selectedIndex}
        onChange={(id) => setTab(id as "active" | "complete")}
      />

      <PagedTable
        key="core-stock"
        url="/rs/stock"
        
        columns={cols}
        pageMode={PageMode.Stock} // does nothing for this table
        queryLabel="Search any vehicle details"
        onRowClick={(row: IPart) => {
          openInNewTab(`/stock/${row.id}`);
        }}
        actionButtons={[
          { label: "Complete", onClick: (rows: RowModel<IPart>) => {
            var partIds = rows.flatRows.map(row => row.original.id);
            bulkCompleteStock.mutate({ ids: partIds}, {
              onSuccess: (res: any) => {
                saveHandlers.onSuccess();
              },
              onError: (res: any) => {
                saveHandlers.onError();
              }
            });
          } ,disabledOnNoRows: true},
        ]} 
        showTotal={true}
        additionalBody={tab == "active" ? {} : { partStatusNotEqual : null, partStatus: PartStatus.Sold }}
        // set the initial sort (SortingState) to scheduledCollectionOn
        rowColor={(row: IPart) => {
          return row.partStatus != PartStatus.Sold ? "" : "bg-green-100 group-hover:!bg-green-200"
        }}
        initialSort={[{ id: "created", desc: true }]}
      />
    </>
  )
}